import { supabase } from "@/lib/supabase/client";
import { type SearchSort } from "@/lib/searchCatalog";

/**
 * Data layer Hasil Pencarian SISI-SERVER (Tahap 2). Memanggil RPC
 * `search_public_products` (mig 159) yang melakukan filter (nama/merek/kategori/
 * isi varian), paginasi, urut, dan faset di DB — klien tak lagi mengunduh
 * seluruh katalog. Dipakai `SearchClient`. Anon-safe (RPC `grant ... to anon`).
 */

export type SearchParams = {
  query: string;
  /** Faset MULTI-pilih (mig 169) — OR dalam dimensi, AND antar dimensi. */
  brands: string[];
  categories: string[];
  segments: string[];
  /** Faset ukuran (mig 197, dari opsi varian grup Ukuran/Size); absen = tanpa filter. */
  sizes?: string[];
  type: "preorder" | "ready_stock" | null;
  sort: SearchSort;
  limit: number;
  offset: number;
  /** MULTI-TENANT FASE 4 (mig 3130): toko hasil resolusi Host — di-thread dari
   * server page → prop klien. Absen/null = perilaku lama (escape). */
  storeId?: string | null;
};

export type SearchResult = {
  /** Id produk halaman ini (urut sesuai `sort`), dihidrasi terpisah utk kartu. */
  ids: string[];
  /** Faset merek (unik case-insensitive, urut) di antara hasil — abaikan filter merek. */
  brands: string[];
  /** Faset kategori (unik case-insensitive, urut) — abaikan filter kategori. */
  categories: string[];
  /** Faset segmen (unik case-insensitive, urut) — abaikan filter segmen. */
  segments: string[];
  /** Faset ukuran (unik case-insensitive; urutkan di klien via `sortSizeLabels`). */
  sizes: string[];
  /** Total produk yang cocok seluruh kriteria (untuk hitung & "ada lagi"). */
  total: number;
};

const EMPTY: SearchResult = {
  ids: [],
  brands: [],
  categories: [],
  segments: [],
  sizes: [],
  total: 0,
};

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

/**
 * Plafon `p_limit` RPC `search_public_products` (`least(…, 60)`, mig 3550 —
 * dicek di PROD 2026-10-28). Pemulihan daftar yang meminta lebih dari ini
 * memecah permintaannya (`restoreChunks`).
 */
export const SEARCH_PUBLIC_MAX_LIMIT = 60;

export async function searchPublicProducts(
  params: SearchParams
): Promise<SearchResult> {
  if (!supabase) return EMPTY;

  const { data, error } = await supabase.rpc("search_public_products", {
    p_query: params.query,
    p_brands: params.brands.length ? params.brands : null,
    p_categories: params.categories.length ? params.categories : null,
    p_segments: params.segments.length ? params.segments : null,
    p_sizes: params.sizes?.length ? params.sizes : null,
    p_type: params.type,
    p_sort: params.sort,
    p_limit: params.limit,
    p_offset: params.offset,
    p_store_id: params.storeId ?? null,
  });

  if (error || !data) return EMPTY;

  const obj = data as {
    ids?: unknown;
    brands?: unknown;
    categories?: unknown;
    segments?: unknown;
    sizes?: unknown;
    total?: unknown;
  };
  return {
    ids: asStringArray(obj.ids),
    brands: asStringArray(obj.brands),
    categories: asStringArray(obj.categories),
    segments: asStringArray(obj.segments),
    sizes: asStringArray(obj.sizes),
    total: typeof obj.total === "number" ? obj.total : 0,
  };
}

/**
 * Saran/autocomplete: nama produk discoverable teratas (paling relevan) yang
 * cocok kata kunci, untuk dropdown saat mengetik (RPC `search_public_suggest`,
 * mig 160). Toleran error → [].
 */
export async function searchSuggest(
  query: string,
  limit = 6,
  storeId?: string | null
): Promise<string[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc("search_public_suggest", {
    p_query: query,
    p_limit: limit,
    p_store_id: storeId ?? null,
  });
  if (error || !data) return [];
  return asStringArray(data);
}
